/**
 * StatsLiveScreen — assembled live mode layout.
 *
 *   ┌──────────────────────────────────────────────┐
 *   │ HEADER  back  team   score   period  save    │
 *   ├──────┬───────────────────────┬───────────────┤
 *   │      │                       │               │
 *   │BENCH │   PeriodClock + Score │  ActionsPanel │
 *   │      │                       │               │
 *   │      │                       │               │
 *   └──────┴───────────────────────┴───────────────┘
 *
 * The screen is a pure assembly: it receives all hooks + handlers from
 * StatsRouter via props.
 */

import React from 'react';
import { Box, IconButton, Typography, Tooltip } from '@mui/material';
import ArrowBackIcon from '@mui/icons-material/ArrowBack';
import SaveIcon from '@mui/icons-material/Save';
import UndoIcon from '@mui/icons-material/Undo';
import { useStatsTheme, withAlpha } from '../theme/statsTheme';
import {
  PeriodClock,
  ScorePanel,
  ActionsPanel,
  BenchDrawer,
  MoreActionsSheet,
  ConnectivityChip,
} from '../components';

export default function StatsLiveScreen({
  // Game info
  currentGame,
  teamName,

  // Roster
  players,
  activePlayers,
  playerTimeTracking,
  timeOnFloorByPlayerId,
  foulsByPlayerId,
  onPromote,
  onSubstitute,

  // Clock
  currentPeriod,
  timeLeft,
  isRunning,
  periodStarted,
  onStart,
  onPause,
  onEnd,

  // Score
  puntuacionLocal,
  puntuacionVisitante,
  onLocalIncrement,
  onLocalDecrement,
  onOpponentIncrement,
  onOpponentDecrement,
  onOpponentQuickScore,

  // Actions
  onAction,
  onUndo,
  hasLastAction,

  // Tags & qualifiers
  tags,
  activeQualifiers,
  onTagClick,
  onToggleQualifier,

  // More actions sheet
  moreSheetOpen,
  moreSheetPlayer,
  onOpenMoreSheet,
  onCloseMoreSheet,
  onMoreSheetAction,

  // UI
  benchCollapsed,
  onToggleBench,

  // DnD render-prop hooks (Phase 3.5)
  renderBenchItem,
  renderActiveCard,

  // Save & exit
  isOnline,
  isSaving,
  saveFailed,
  onSave,
  onExit,

  t,
}) {
  const theme = useStatsTheme();
  const tt = (key, fallback) => (typeof t === 'function' ? t(`estadisticasNew.${key}`, { defaultValue: fallback }) : fallback);

  return (
    <Box
      sx={{
        height: '100vh',
        display: 'flex',
        flexDirection: 'column',
        backgroundColor: theme.surfaces.base,
        color: theme.text.primary,
        fontFamily: theme.typography.fontFamily,
        overflow: 'hidden',
      }}
    >
      {/* HEADER */}
      <Box
        sx={{
          height: 56,
          flexShrink: 0,
          display: 'flex',
          alignItems: 'center',
          gap: 1.5,
          px: 1.5,
          borderBottom: `1px solid ${theme.surfaces.border}`,
          backgroundColor: theme.surfaces.elevated,
        }}
      >
        <IconButton onClick={onExit} aria-label="back" sx={{ color: theme.text.primary }}>
          <ArrowBackIcon />
        </IconButton>
        <Box sx={{ flex: 1, minWidth: 0 }}>
          <Typography sx={{ fontSize: 14, fontWeight: 700, color: theme.text.primary }} noWrap>
            {currentGame?.game_label || teamName}
            {currentGame?.adversary_name && (
              <Typography component="span" sx={{ ml: 0.6, color: theme.text.secondary, fontWeight: 400 }}>
                vs {currentGame.adversary_name}
              </Typography>
            )}
          </Typography>
        </Box>

        <Tooltip title={tt('undoLast', 'Undo last action')}>
          <span>
            <IconButton
              onClick={onUndo}
              disabled={!hasLastAction}
              aria-label="undo"
              sx={{
                color: hasLastAction ? theme.text.primary : theme.text.muted,
                opacity: hasLastAction ? 1 : 0.4,
              }}
            >
              <UndoIcon />
            </IconButton>
          </span>
        </Tooltip>

        <ConnectivityChip
          isOnline={isOnline}
          isSaving={isSaving}
          saveFailed={saveFailed}
          t={t}
        />

        <Tooltip title={tt('save', 'Save')}>
          <IconButton
            onClick={onSave}
            disabled={isSaving}
            aria-label="save"
            sx={{
              color: theme.accent,
              backgroundColor: theme.accentSoft,
              border: `1px solid ${theme.accentBorder}`,
              '&:hover': { backgroundColor: withAlpha(theme.accent, 0.24) },
            }}
          >
            <SaveIcon />
          </IconButton>
        </Tooltip>
      </Box>

      {/* MAIN */}
      <Box sx={{ flex: 1, display: 'flex', minHeight: 0 }}>
        {/* Bench column */}
        <BenchDrawer
          players={players}
          collapsed={benchCollapsed}
          onToggleCollapse={onToggleBench}
          onPromote={onPromote}
          renderItem={renderBenchItem}
          t={t}
        />

        {/* Game console column */}
        <Box
          sx={{
            width: { xs: '100%', md: 360 },
            flexShrink: 0,
            p: 2,
            display: 'flex',
            flexDirection: 'column',
            gap: 2,
            borderRight: `1px solid ${theme.surfaces.border}`,
            backgroundColor: theme.surfaces.base,
            overflow: 'auto',
          }}
        >
          <PeriodClock
            currentPeriod={currentPeriod}
            numberOfPeriods={currentGame?.number_of_periods || 4}
            timeLeft={timeLeft}
            isRunning={isRunning}
            periodStarted={periodStarted}
            onStart={onStart}
            onPause={onPause}
            onEnd={onEnd}
            t={t}
          />
          <ScorePanel
            teamName={teamName}
            opponentName={currentGame?.adversary_name}
            localScore={puntuacionLocal}
            opponentScore={puntuacionVisitante}
            onLocalIncrement={onLocalIncrement}
            onLocalDecrement={onLocalDecrement}
            onOpponentIncrement={onOpponentIncrement}
            onOpponentDecrement={onOpponentDecrement}
            onOpponentQuickScore={onOpponentQuickScore}
          />
        </Box>

        {/* Actions panel column */}
        <Box sx={{ flex: 1, minWidth: 0 }}>
          <ActionsPanel
            activePlayers={activePlayers}
            tags={tags}
            activeQualifiers={activeQualifiers}
            onAction={onAction}
            onTagClick={onTagClick}
            onToggleQualifier={onToggleQualifier}
            onMore={onOpenMoreSheet}
            onSubstitute={onSubstitute}
            playerTimeTracking={playerTimeTracking}
            timeOnFloorByPlayerId={timeOnFloorByPlayerId}
            fouls={foulsByPlayerId}
            renderPlayerCard={renderActiveCard}
            t={t}
          />
        </Box>
      </Box>

      <MoreActionsSheet
        open={moreSheetOpen}
        onClose={onCloseMoreSheet}
        player={moreSheetPlayer}
        onAction={onMoreSheetAction}
        t={t}
      />
    </Box>
  );
}
